/**
 * The colonnade, and the garden at the end of it.
 *
 * A one-point perspective scene drawn on a 2D canvas. Everything is a
 * billboard placed at a world depth and scaled by `focal / (z - camera)`.
 * Walking the camera forward is the only animation, and it is the reward.
 *
 * Kept out of the component because it is pure drawing: it takes a context,
 * a size and a camera position, and paints. Nothing here knows what a
 * question is.
 *
 * ── The one thing that must not be forgotten ─────────────────────────────────
 *
 * EYE is the camera's height above the floor, and it is not optional. Without
 * it every floor point (y = 0) projects to `horizon - 0 × scale`, which is the
 * horizon itself: the whole floor collapses into one hard line across the
 * screen and the colonnade appears to be standing on the skyline.
 */

export const GARDEN_Z = 11.2;
export const COLS = 9;
export const COL_SPACING = 1.25;
/** Where the camera stops: short of the doorway, so the garden is a view. */
export const WALK_END = GARDEN_Z - 3.5;

const EYE = 1.62;
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;

/** Deterministic value noise — foliage that varies but never flickers. */
function noise1(x) {
  const i = Math.floor(x);
  const f = x - i;
  const h = (n) => {
    const s = Math.sin(n * 127.1) * 43758.5453;
    return s - Math.floor(s);
  };
  const u = f * f * (3 - 2 * f);
  return lerp(h(i), h(i + 1), u);
}

function makeProjector(camZ, W, horizon, F) {
  return (x, y, z) => {
    const dz = z - camZ;
    if (dz <= 0.12) return null;
    const s = F / dz;
    return { x: W / 2 + x * s, y: horizon - (y - EYE) * s, s };
  };
}

/* ── the garden ─────────────────────────────────────────────────────────── */

function drawGarden(ctx, p, W) {
  const floor = p(0, 0, GARDEN_Z);
  const top = p(0, 3.55, GARDEN_Z);
  const left = p(-2.35, 0, GARDEN_Z);
  if (!floor || !top || !left) return;

  const x0 = left.x;
  const x1 = W - left.x;
  const yTop = top.y;
  const yFloor = floor.y;
  const gh = yFloor - yTop;
  if (gh <= 0) return;

  ctx.save();
  ctx.beginPath(); // seen only through the opening
  ctx.moveTo(x0, yFloor);
  ctx.lineTo(x0, yTop + gh * 0.18);
  ctx.quadraticCurveTo(W / 2, yTop - gh * 0.1, x1, yTop + gh * 0.18);
  ctx.lineTo(x1, yFloor);
  ctx.closePath();
  ctx.clip();

  const sky = ctx.createLinearGradient(0, yTop, 0, yFloor);
  sky.addColorStop(0, '#bfe8ff');
  sky.addColorStop(0.42, '#ffeeba');
  sky.addColorStop(0.62, '#d8f0a0');
  ctx.fillStyle = sky;
  ctx.fillRect(x0 - 4, yTop - gh, x1 - x0 + 8, gh * 2.4);

  const sun = ctx.createRadialGradient(W / 2, yTop + gh * 0.52, 0, W / 2, yTop + gh * 0.52, gh * 0.95);
  sun.addColorStop(0, 'rgba(255,255,236,.98)');
  sun.addColorStop(0.35, 'rgba(255,244,190,.66)');
  sun.addColorStop(1, 'rgba(255,240,180,0)');
  ctx.fillStyle = sun;
  ctx.fillRect(x0 - 4, yTop - gh, x1 - x0 + 8, gh * 2.4);

  /*
   * Three ranks of trees, palest and highest at the back, drawn as overlapping
   * crowns rather than one smooth noise curve. A smooth silhouette against a
   * bright sky reads as distant hills, which is the one thing this must not be.
   */
  const bands = [
    { y: yTop + gh * 0.42, amp: gh * 0.085, col: 'rgba(158,204,128,.80)', gap: gh * 0.075, seed: 3.1 },
    { y: yTop + gh * 0.54, amp: gh * 0.115, col: 'rgba(104,166,78,.92)', gap: gh * 0.095, seed: 8.7 },
    { y: yTop + gh * 0.66, amp: gh * 0.15, col: '#3f7f34', gap: gh * 0.125, seed: 15.4 },
  ];
  for (const b of bands) {
    ctx.fillStyle = b.col;
    ctx.fillRect(x0 - 6, b.y, x1 - x0 + 12, yFloor - b.y + gh);
    for (let x = x0 - b.gap; x <= x1 + b.gap; x += b.gap) {
      const n = noise1((x / b.gap) * 0.9 + b.seed);
      const r = b.amp * (0.55 + n * 0.85);
      ctx.beginPath();
      ctx.ellipse(x, b.y - r * 0.34, r * 1.12, r, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const grass = ctx.createLinearGradient(0, yTop + gh * 0.66, 0, yFloor + gh * 0.4);
  grass.addColorStop(0, '#4d9440');
  grass.addColorStop(1, '#7fcc5c');
  ctx.fillStyle = grass;
  ctx.fillRect(x0 - 6, yTop + gh * 0.66, x1 - x0 + 12, yFloor - (yTop + gh * 0.66) + gh * 0.5);

  /*
   * The tomb sits high in the opening on purpose. Placed naturalistically —
   * low, where the ground is — it ends up behind the panel on the arrival
   * screen, hiding the one object the whole walk is toward.
   */
  const tx = W / 2 + (x1 - x0) * 0.19;
  const ty = yTop + gh * 0.6;
  const tw = gh * 0.2;
  ctx.fillStyle = '#6a6459'; // the rock face
  ctx.beginPath();
  ctx.moveTo(tx - tw, ty + tw * 0.62);
  ctx.quadraticCurveTo(tx - tw * 1.04, ty - tw * 0.72, tx, ty - tw * 0.92);
  ctx.quadraticCurveTo(tx + tw * 1.04, ty - tw * 0.72, tx + tw, ty + tw * 0.62);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#7e7669'; // a lit shoulder of rock
  ctx.beginPath();
  ctx.moveTo(tx - tw * 0.1, ty - tw * 0.9);
  ctx.quadraticCurveTo(tx + tw * 1.02, ty - tw * 0.7, tx + tw, ty + tw * 0.62);
  ctx.lineTo(tx + tw * 0.5, ty + tw * 0.62);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#14181d'; // the door, standing open
  ctx.beginPath();
  ctx.moveTo(tx - tw * 0.36, ty + tw * 0.62);
  ctx.quadraticCurveTo(tx - tw * 0.38, ty - tw * 0.24, tx, ty - tw * 0.34);
  ctx.quadraticCurveTo(tx + tw * 0.38, ty - tw * 0.24, tx + tw * 0.36, ty + tw * 0.62);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#8d8478'; // the stone, rolled aside
  ctx.beginPath();
  ctx.ellipse(tx - tw * 0.92, ty + tw * 0.3, tw * 0.34, tw * 0.36, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(0,0,0,.16)';
  ctx.beginPath();
  ctx.ellipse(tx - tw * 0.92, ty + tw * 0.62, tw * 0.38, tw * 0.09, 0, 0, Math.PI * 2);
  ctx.fill();

  // two nearer trees, with trunks, so the garden has a foreground too
  for (const [fx, fs] of [
    [x0 + (x1 - x0) * 0.11, 1],
    [x0 + (x1 - x0) * 0.92, 0.82],
  ]) {
    const cy = yTop + gh * 0.6;
    const r = gh * 0.15 * fs;
    ctx.fillStyle = '#4a3524';
    ctx.fillRect(fx - r * 0.1, cy - r * 0.2, r * 0.2, gh * 0.34);
    ctx.fillStyle = '#357029';
    for (const [ox, oy, orr] of [
      [-0.62, 0.12, 0.62],
      [0.6, 0.16, 0.58],
      [0, -0.44, 0.76],
      [-0.22, 0.3, 0.54],
      [0.28, 0.28, 0.5],
    ]) {
      ctx.beginPath();
      ctx.ellipse(fx + ox * r, cy - r * 0.5 + oy * r, orr * r, orr * r * 0.92, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

/* ── a Roman column ─────────────────────────────────────────────────────── */

function drawColumn(ctx, p, x, z, lit) {
  const base = p(x, 0, z);
  const top = p(x, 3.05, z);
  if (!base || !top) return;
  const w = 0.3 * base.s;
  const shade = 26 + lit * 30;

  const g = ctx.createLinearGradient(base.x - w, 0, base.x + w, 0);
  g.addColorStop(0, `rgb(${(shade * 0.5) | 0},${(shade * 0.54) | 0},${(shade * 0.62) | 0})`);
  g.addColorStop(0.45, `rgb(${shade | 0},${(shade * 1.04) | 0},${(shade * 1.14) | 0})`);
  g.addColorStop(1, `rgb(${(shade * 0.4) | 0},${(shade * 0.44) | 0},${(shade * 0.52) | 0})`);
  ctx.fillStyle = g;
  ctx.fillRect(base.x - w / 2, top.y, w, base.y - top.y);

  ctx.fillStyle = 'rgba(0,0,0,.22)'; // fluting
  for (const f of [-0.22, 0, 0.22]) {
    ctx.fillRect(base.x + f * w - w * 0.02, top.y, w * 0.04, base.y - top.y);
  }
  ctx.fillStyle = `rgb(${(shade * 1.25) | 0},${(shade * 1.28) | 0},${(shade * 1.36) | 0})`;
  ctx.fillRect(base.x - w * 0.74, top.y, w * 1.48, w * 0.3);
  ctx.fillRect(base.x - w * 0.66, top.y + w * 0.3, w * 1.32, w * 0.1);
  ctx.fillRect(base.x - w * 0.64, base.y - w * 0.24, w * 1.28, w * 0.24);
}

/* ── a Roman soldier ────────────────────────────────────────────────────── */

function drawSoldier(ctx, p, x, z, lit, bar) {
  const base = p(x, 0, z);
  const head = p(x, 1.78, z);
  if (!base || !head) return;
  const h = base.y - head.y;
  if (h < 4) return;
  const w = h * 0.22;
  const dark = `rgba(9,11,15,${clamp(0.55 + lit * 0.45, 0.55, 1)})`;

  ctx.save();
  // The cloak is the one warm colour in the corridor, and the garden light
  // eventually swamps it — which is the whole arc of the walk in one detail.
  ctx.fillStyle = `rgba(${(120 - lit * 24) | 0},${(38 - lit * 8) | 0},${(28 - lit * 6) | 0},${clamp(
    0.3 + lit * 0.5,
    0.3,
    0.85,
  )})`;
  ctx.beginPath();
  ctx.moveTo(base.x - w * 0.34, head.y + h * 0.14);
  ctx.bezierCurveTo(base.x - w * 0.92, head.y + h * 0.26, base.x - w * 1.02, base.y - h * 0.3, base.x - w * 0.86, base.y - h * 0.02);
  ctx.lineTo(base.x + w * 0.86, base.y - h * 0.02);
  ctx.bezierCurveTo(base.x + w * 1.02, base.y - h * 0.3, base.x + w * 0.92, head.y + h * 0.26, base.x + w * 0.34, head.y + h * 0.14);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = dark;
  ctx.beginPath(); // legs
  ctx.moveTo(base.x - w * 0.36, base.y);
  ctx.lineTo(base.x - w * 0.34, base.y - h * 0.42);
  ctx.lineTo(base.x + w * 0.34, base.y - h * 0.42);
  ctx.lineTo(base.x + w * 0.36, base.y);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath(); // torso, armoured
  ctx.moveTo(base.x - w * 0.44, base.y - h * 0.4);
  ctx.lineTo(base.x - w * 0.52, head.y + h * 0.2);
  ctx.lineTo(base.x + w * 0.52, head.y + h * 0.2);
  ctx.lineTo(base.x + w * 0.44, base.y - h * 0.4);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath(); // head
  ctx.ellipse(base.x, head.y + h * 0.09, w * 0.26, w * 0.3, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath(); // helmet bowl and neck guard
  ctx.moveTo(base.x - w * 0.32, head.y + h * 0.08);
  ctx.bezierCurveTo(base.x - w * 0.32, head.y - h * 0.08, base.x + w * 0.32, head.y - h * 0.08, base.x + w * 0.32, head.y + h * 0.08);
  ctx.lineTo(base.x + w * 0.38, head.y + h * 0.14);
  ctx.lineTo(base.x - w * 0.38, head.y + h * 0.14);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath(); // transverse crest
  ctx.moveTo(base.x - w * 0.4, head.y - h * 0.05);
  ctx.quadraticCurveTo(base.x, head.y - h * 0.2, base.x + w * 0.4, head.y - h * 0.05);
  ctx.quadraticCurveTo(base.x, head.y - h * 0.1, base.x - w * 0.4, head.y - h * 0.05);
  ctx.closePath();
  ctx.fill();

  const shieldSide = x < 0 ? 1 : -1;
  const sx = base.x + shieldSide * w * 0.62;
  ctx.fillStyle = dark;
  ctx.beginPath(); // the scutum
  ctx.moveTo(sx - w * 0.3, base.y - h * 0.62);
  ctx.quadraticCurveTo(sx, base.y - h * 0.7, sx + w * 0.3, base.y - h * 0.62);
  ctx.lineTo(sx + w * 0.3, base.y - h * 0.1);
  ctx.quadraticCurveTo(sx, base.y - h * 0.02, sx - w * 0.3, base.y - h * 0.1);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = `rgba(190,150,70,${0.28 + lit * 0.4})`;
  ctx.beginPath();
  ctx.ellipse(sx, base.y - h * 0.36, w * 0.075, w * 0.085, 0, 0, Math.PI * 2);
  ctx.fill();

  /*
   * The pilum: upright at rest, lowered across the way when barred.
   *
   * Lowered to about 62 degrees, not to the horizontal. Flat, with a
   * triangular head on the end, it stopped reading as a spear at all and
   * became a red arrow drawn across the screen.
   */
  ctx.translate(base.x - shieldSide * w * 0.86, base.y - h * 0.06);
  ctx.rotate(bar ? bar * 1.08 * (x < 0 ? 1 : -1) : 0);
  ctx.lineCap = 'round';
  /*
   * A lowered spear is still iron. Drawn in flat red it read as a graphic
   * arrow laid over the scene, so the danger is a glow behind the shaft
   * rather than the colour of the shaft itself.
   */
  if (bar) {
    ctx.save();
    ctx.strokeStyle = `rgba(214,86,58,${0.5 * bar})`;
    ctx.lineWidth = Math.max(3, h * 0.1);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -h * 1.2);
    ctx.stroke();
    ctx.restore();
  }
  ctx.strokeStyle = bar ? `rgba(38,26,24,${0.8 + 0.2 * bar})` : dark;
  ctx.lineWidth = Math.max(1, h * 0.034);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, -h * 1.2);
  ctx.stroke();
  ctx.fillStyle = ctx.strokeStyle;
  ctx.beginPath();
  ctx.moveTo(0, -h * 1.36);
  ctx.lineTo(-h * 0.038, -h * 1.18);
  ctx.lineTo(h * 0.038, -h * 1.18);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/**
 * Paint one frame.
 *
 * @param ctx    2D context, already scaled for device pixel ratio
 * @param W,H    canvas size in CSS pixels
 * @param camZ   how far down the colonnade the camera has walked
 * @param bob    a small vertical wobble, so a step has a rise and fall to it
 * @param barAt  timestamp a pilum came down, or 0
 * @param now    the current frame time
 */
export function drawHall(ctx, W, H, { camZ, bob = 0, barAt = 0, now = 0 }) {
  // how much of the corridor the morning has reached
  const dawn = clamp(camZ / WALK_END, 0, 1);
  const horizon = H * 0.4 + bob * H;
  const F = W * 0.92;
  const p = makeProjector(camZ, W, horizon, F);

  ctx.fillStyle = '#05070b';
  ctx.fillRect(0, 0, W, H);

  const cg = ctx.createLinearGradient(0, 0, 0, horizon);
  cg.addColorStop(0, '#080a10');
  cg.addColorStop(1, `rgb(${(18 + dawn * 26) | 0},${(22 + dawn * 30) | 0},${(28 + dawn * 26) | 0})`);
  ctx.fillStyle = cg;
  ctx.fillRect(0, 0, W, horizon);

  const fg = ctx.createLinearGradient(0, horizon, 0, H);
  fg.addColorStop(0, `rgb(${(26 + dawn * 30) | 0},${(28 + dawn * 32) | 0},${(34 + dawn * 28) | 0})`);
  fg.addColorStop(1, '#080a0e');
  ctx.fillStyle = fg;
  ctx.fillRect(0, horizon, W, H - horizon);

  /*
   * Light spilling out of the garden and down the flagstones — the only thing
   * that changes as you walk, and what the walk is for.
   *
   * It is laid down BEFORE the garden, not after. Painted over the top it
   * washes the greens out to pale grey exactly at the moment they should be
   * most vivid, and the tomb turns into a bright smudge.
   */
  const spill = p(0, 0, GARDEN_Z);
  if (spill) {
    const sg = ctx.createRadialGradient(W / 2, spill.y, 0, W / 2, spill.y, Math.max(W, H) * (0.35 + dawn * 0.85));
    sg.addColorStop(0, `rgba(228,246,190,${0.3 + dawn * 0.45})`);
    sg.addColorStop(0.35, `rgba(198,232,150,${0.12 + dawn * 0.26})`);
    sg.addColorStop(1, 'rgba(150,200,120,0)');
    ctx.fillStyle = sg;
    ctx.fillRect(0, 0, W, H);
  }

  drawGarden(ctx, p, W);

  // ceiling beams and floor bands, so movement is visible above and below
  for (let b = 0; b < 30; b += 1) {
    const bz = Math.ceil(camZ * 2) / 2 + b * 0.5;
    if (bz > GARDEN_Z) break;
    const fade = clamp(1 - (bz - camZ) * 0.1, 0.1, 1);
    for (const [yy, col] of [
      [3.55, `rgba(20,24,30,${0.85 * fade})`],
      [0, `rgba(214,224,206,${0.05 * fade})`],
    ]) {
      const a = p(2.35, yy, bz);
      const c = p(2.35, yy, bz + 0.13);
      if (!a || !c) continue;
      ctx.fillStyle = col;
      ctx.beginPath();
      ctx.moveTo(W - a.x, a.y);
      ctx.lineTo(a.x, a.y);
      ctx.lineTo(c.x, c.y);
      ctx.lineTo(W - c.x, c.y);
      ctx.closePath();
      ctx.fill();
    }
  }

  // far to near, so nearer things overlap correctly
  for (let i = COLS; i >= 1; i -= 1) {
    const z = 1 + i * COL_SPACING;
    const d = z - camZ;
    const lit = clamp(1.25 - d * 0.1, 0.1, 1);
    for (const side of [-1, 1]) {
      drawColumn(ctx, p, side * 1.55, z, lit);
      // one soldier a bay, alternating sides so the way is not a mirror
      if (Math.round((z - 1) / COL_SPACING) % 2 === (side < 0 ? 0 : 1)) continue;
      /*
       * Bar the way a little further down, never at the nearest bay.
       *
       * The soldier next to the camera is drawn almost the height of the
       * screen, so his pilum swung flat became a red bar clean across the
       * frame — it read as a user-interface arrow rather than a spear. Two
       * bays ahead they are the size of people and it reads as the way being
       * blocked, which is what it is.
       *
       * The window spans three bays, and each bay holds a soldier on the
       * alternate side, so the lowered spears cross the way from both sides
       * instead of one lying flat across it.
       */
      const bar =
        barAt && d > 3.0 && d < 6.0
          ? clamp((now - barAt) / 220, 0, 1) * (now - barAt < 1500 ? 1 : Math.max(0, 1 - (now - barAt - 1500) / 400))
          : 0;
      drawSoldier(ctx, p, side * 1.12, z, lit, bar);
    }
  }

  const vg = ctx.createRadialGradient(W / 2, horizon, Math.min(W, H) * 0.2, W / 2, horizon, Math.max(W, H) * 0.85);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, `rgba(0,0,0,${0.86 - dawn * 0.22})`);
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, W, H);
}
